import { Dialog, TablePagination } from "@mui/material";
import styles from "./smartdevices.module.css";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { TableLoader } from "../../../../../components/LoaderC";
import DeviceCard from "./components/DeviceCard";
import DeviceFilter from "./components/DeviceFilter";
import AddDeviceForm from "./components/AddDeviceForm";
import AddLockForm from "./components/AddLockForm";
import { toast } from "react-smart-toaster";
import useDebouncedValue from "../../../../../hooks/useDebouncedValue";

const SmartDevices = ({ tenantData, setLoading, setSnackbarOpen, setSnackbarMsg }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [contentLoading, setContentLoading] = useState(true);
    const [deviceList, setDeviceList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadError, setLoadError] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(6);
    const [filterData, setFilterData] = useState({ searchKey: "", product: "", location: "", status: "" });
    const [showAddFormNew, setShowAddFormNew] = useState(false);
    const [showAddFormNew2, setShowAddFormNew2] = useState(false);
    const latestRequest = useRef(0);

    // Debounce search so we don't hit the API on every keystroke
    const searchKey = useDebouncedValue(filterData.searchKey, 400);
    const aptId = tenantData?.apt_id;

    useEffect(() => {
        if (!aptId) return;

        const requestId = ++latestRequest.current;

        const fetchDevices = async () => {
            try {
                const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

                const response = await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/devices/" + aptId + "/paginated", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        page: page + 1,
                        per_page: perPage === -1 ? "all" : perPage,
                        search: searchKey.trim() || undefined,
                        status: filterData.status !== "" ? filterData.status : undefined,
                        location: filterData.location || undefined,
                        product_type: filterData.product || undefined,
                    },
                });

                // Ignore responses from requests that were superseded by newer filters/pages
                if (requestId !== latestRequest.current) return;

                const rows = response.data?.data || [];
                const total = response.data?.meta?.total || 0;

                // The current page can become empty (e.g. devices removed elsewhere), so step back to the new last page
                if (rows.length === 0 && page > 0 && total > 0) {
                    setPage(Math.max(0, (response.data?.meta?.last_page ?? 1) - 1));
                    return;
                }

                setDeviceList(rows);
                setTotalCount(total);
                setLoadError(false);
            } catch (error) {
                if (requestId !== latestRequest.current) return;
                console.error("[SmartDevices] Failed to load device list", error);
                setLoadError(true);
                setDeviceList([]);
                setTotalCount(0);
                toast.error(error?.response?.data?.message || "Unable to load devices. Please try again.");
            } finally {
                if (requestId === latestRequest.current)
                    setContentLoading(false);
            }
        };

        fetchDevices();
    }, [getAccessTokenSilently, aptId, page, perPage, searchKey, filterData.status, filterData.location, filterData.product, reloadKey]);

    // The list is paginated on the server, so reload it after adding devices
    const onAdd = () => {
        setPage(0);
        setReloadKey((prev) => prev + 1);
    };

    return <>
        {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
        </div>)}

        {!contentLoading && <>

            <DeviceFilter filterData={filterData} setFilterData={setFilterData.bind(this)} setPage={setPage.bind(this)} setShowAddFormNew={setShowAddFormNew.bind(this)} setShowAddFormNew2={setShowAddFormNew2.bind(this)} />

            <div className={`${styles.SmartDeviceRow}`}>

                {deviceList.map((item) => {
                    return (<DeviceCard key={item?.id} item={item} />);
                })}

                {deviceList.length > 0 && (<div className="Pagination TablePaginationNew List">
                    <TablePagination
                        count={totalCount}
                        color="primary"
                        page={page}
                        rowsPerPage={perPage}
                        onPageChange={(e, v) => setPage(v)}
                        rowsPerPageOptions={[
                            { label: "6", value: 6 },
                            { label: "12", value: 12 },
                            { label: "18", value: 18 },
                            { label: "All", value: -1 },
                        ]}
                        onRowsPerPageChange={(e) => {
                            setPage(0);
                            setPerPage(e.target.value);
                        }}
                        labelRowsPerPage={"Devices per Page:"}
                    />
                </div>)}

                {deviceList.length === 0 && loadError && (<div className={`${styles.NoDataMain}`}>
                    <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                    <p className={`${styles.NoDataTextSub}`}>We couldn't load devices. Please try again.</p>
                    <button type="button" className={`${styles.RetryBu}`} onClick={() => setReloadKey((prev) => prev + 1)}>Retry</button>
                </div>)}

                {deviceList.length === 0 && !loadError && (<div className={`${styles.NoDataMain}`}>
                    <div className={`${styles.NoDataIMG}`}>
                        <img src="/static/img/noDevice.svg" alt="No Data" />
                    </div>
                    <p className={`${styles.NoDataText}`}>Let's fill it up!</p>
                    <p className={`${styles.NoDataTextSub}`}>Device List is Currently Empty.</p>
                </div>)}

            </div>
        </>}

        <Dialog
            open={showAddFormNew}
            onClose={(e) => { setShowAddFormNew(false); }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp MobileForm CustomWidthPops"
        >
            <AddDeviceForm
                setShowAddFormNew={setShowAddFormNew.bind(this)}
                setSnackbarOpen={setSnackbarOpen.bind(this)}
                setSnackbarMsg={setSnackbarMsg.bind(this)}
                setLoading={setLoading.bind(this)}
                tenantData={tenantData}
                onSubmitLocal={onAdd.bind(this)}
            />
        </Dialog>

        <Dialog
            open={showAddFormNew2}
            onClose={(e) => { setShowAddFormNew2(false); }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="NewPopUp MobileForm CustomWidthPops"
        >
            <AddLockForm
                setShowAddFormNew={setShowAddFormNew2.bind(this)}
                setSnackbarOpen={setSnackbarOpen.bind(this)}
                setSnackbarMsg={setSnackbarMsg.bind(this)}
                setLoading={setLoading.bind(this)}
                tenantData={tenantData}
                onSubmitLocal={onAdd.bind(this)}
            />
        </Dialog>
    </>;
}

export default SmartDevices;